import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BookCartProvider } from './context/BookCartContext';
import navigationRef from './utils/navigationRef';
import Footer from './components/common/Footer';
import Home from './pages/Home';
import Profile from './pages/Profile';

// Footer Pages
import About from './pages/About';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import RefundPolicy from './pages/RefundPolicy';

// Forgot Password Pages
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Lazy load components for better performance
const Login = lazy(() => import('./components/auth/Login'));
const Register = lazy(() => import('./components/auth/Register'));
const VerifyOTP = lazy(() => import('./components/auth/VerifyOTP'));
const Header = lazy(() => import('./components/common/Header'));
const UniversityStudyMaterial = lazy(() => import('./pages/UniversityStudyMaterial'));
const SessionListPage = lazy(() => import('./pages/SessionListPage'));
const SemesterListPage = lazy(() => import('./pages/SemesterListPage'));
const BooksListPage = lazy(() => import('./pages/BooksListPage'));
const BookDetailPage = lazy(() => import('./pages/BookDetailPage'));
const BookCartPage = lazy(() => import('./pages/BookCartPage'));
const BookCheckoutPage = lazy(() => import('./pages/BookCheckoutPage'));
const MyBookPurchases = lazy(() => import('./pages/MyBookPurchases'));
const MyPurchasesAll = lazy(() => import('./pages/MyPurchasesAll'));
const PDFViewer = lazy(() => import('./pages/PDFViewer'));
const BoardPracticeSets = lazy(() => import('./pages/BoardPracticeSets'));
const TestSeriesListing = lazy(() => import('./pages/TestSeriesListing'));
const BoardCheckout = lazy(() => import('./pages/BoardCheckout'));
const BoardQuiz = lazy(() => import('./pages/BoardQuiz'));
const BoardLeaderboard = lazy(() => import('./pages/BoardLeaderboard'));
// MCQPractice.jsx stays on disk (Phase 6 deletes it) — Step 7 cutover removed
// its route below; Board Practice Sets (above) is now the primary MCQ flow.
// LeaderboardHub replaces MockTestLeaderboard at /leaderboard (Step 4B) — that
// old hardcoded exam-picker stays on disk, still reachable at its own old
// route below, just no longer what the global nav link shows (Phase 6 deletes it).
const LeaderboardHub = lazy(() => import('./pages/LeaderboardHub'));
// MockTests.jsx/MockTestWindow.jsx stay on disk (Phase 6 deletes them) — Step 6
// cutover removed their routes below; the new category-based flow (Step 4-5B)
// is now primary.
const MockTestResult = lazy(() => import('./pages/MockTestResult'));
const MockTestLeaderboard = lazy(() => import('./pages/MockTestLeaderboard'));
const MockLeaderboard = lazy(() => import('./pages/MockLeaderboard'));
const MockTestCategories = lazy(() => import('./pages/MockTestCategories'));
const MockTestListing = lazy(() => import('./pages/MockTestListing'));
const MockCheckout = lazy(() => import('./pages/MockCheckout'));
const MockTestQuizNew = lazy(() => import('./pages/MockTestQuizNew'));
const Blogs = lazy(() => import('./pages/Blogs'));
const BlogDetail = lazy(() => import('./pages/BlogDetail'));

// Scroll to top on route change
const ScrollToTop = () => {
    const { pathname } = useLocation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    return null;
};

// Exposes react-router's navigate() to code outside the Router tree (AuthContext's
// requireAuth), since AuthProvider wraps Router rather than sitting inside it.
const NavigationSetter = () => {
    const navigate = useNavigate();

    useEffect(() => {
        navigationRef.current = navigate;
        return () => {
            navigationRef.current = null;
        };
    }, [navigate]);

    return null;
};

// Loading Spinner Component
const LoadingSpinner = () => (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="text-center">
            <div className="relative">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="h-8 w-8 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg animate-pulse"></div>
                </div>
            </div>
            <p className="mt-4 text-gray-600 font-medium">Loading...</p>
        </div>
    </div>
);

// Protected Route component
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return <LoadingSpinner />;
    }

    if (!isAuthenticated) {
        // Preserves a direct/bookmarked/refreshed link the same way requireAuth()
        // does for button-driven flows — Login.jsx already honors ?redirect=.
        const returnTo = encodeURIComponent(location.pathname + location.search);
        return <Navigate to={`/login?redirect=${returnTo}`} replace />;
    }

    return children;
};

// 404 Not Found Page
const NotFound = () => {
    const { isAuthenticated } = useAuth();
    
    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 px-4">
            <div className="text-center">
                <div className="mb-8">
                    <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl shadow-lg">
                        <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                </div>
                <h1 className="text-9xl font-bold text-indigo-600 mb-4">404</h1>
                <h2 className="text-2xl font-semibold text-gray-800 mb-2">Page Not Found</h2>
                <p className="text-gray-500 mb-8">Oops! The page you're looking for doesn't exist.</p>
                <a 
                    href={isAuthenticated ? "/" : "/login"} 
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 shadow-md"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    {isAuthenticated ? 'Go to Dashboard' : 'Go to Login'}
                </a>
            </div>
        </div>
    );
};

// Main App Content with Routes
function AppContent() {
    const { loading } = useAuth();
    
    if (loading) {
        return <LoadingSpinner />;
    }
    
    return (
        <Router>
            <ScrollToTop />
            <NavigationSetter />
            <Suspense fallback={<LoadingSpinner />}>
                <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-50 to-gray-100">
                    <Header />
                    <main className="flex-grow">
                        <Routes>
                            {/* Public Routes - Footer Pages */}
                            <Route path="/about" element={<About />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="/privacy" element={<PrivacyPolicy />} />
                            <Route path="/terms" element={<TermsConditions />} />
                            <Route path="/refund" element={<RefundPolicy />} />
                            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                            
                            {/* Auth Routes */}
                            <Route path="/login" element={<Login />} />
                            <Route path="/register" element={<Register />} />
                            <Route path="/verify-otp" element={<VerifyOTP />} />
                            
                            {/* Forgot Password Routes */}
                            <Route path="/forgot-password" element={<ForgotPassword />} />
                            <Route path="/reset-password/:token" element={<ResetPassword />} />
                            
                            {/* Homepage — public, matches every other browse page (client
                                requirement: login only at buy/pay/profile, not browsing).
                                /dashboard was the same Home component behind a login gate;
                                now just redirects to the public homepage. */}
                            <Route path="/" element={<Home />} />
                            <Route path="/dashboard" element={<Navigate to="/" replace />} />

                            {/* University Study Material browse flow (public). Step 7B cutover: old
                                /study-material route removed — StudyMaterial.jsx already deleted
                                (Phase 6 cleanup Batch 1). */}
                            <Route path="/university-study-material" element={<UniversityStudyMaterial />} />
                            <Route path="/university-study-material/:universityId" element={<SessionListPage />} />
                            <Route path="/university-study-material/:universityId/:sessionId" element={<SemesterListPage />} />
                            <Route path="/university-study-material/:universityId/:sessionId/:semesterId" element={<BooksListPage />} />
                            <Route path="/book/:bookId" element={<BookDetailPage />} />
                            <Route path="/book-cart" element={<BookCartPage />} />
                            <Route path="/book-checkout" element={<BookCheckoutPage />} />
                            <Route path="/my-book-purchases" element={<ProtectedRoute><MyBookPurchases /></ProtectedRoute>} />
                            {/* Unified purchase history (Phase 4 Step 4A) — parallel to
                                /my-book-purchases above (book-only, untouched). Reachable by
                                direct URL only for now; Header's "My Purchases" link and
                                checkout-success redirects still point at the old page.
                                Cutover to swap them is Phase 6. */}
                            <Route path="/my-purchases-all" element={<ProtectedRoute><MyPurchasesAll /></ProtectedRoute>} />
                            {/* In-app view-only PDF viewer (Phase 6 View-only PDF-iii) — replaces
                                the old force-download flow for purchased books. */}
                            <Route path="/read/:bookId" element={<ProtectedRoute><PDFViewer /></ProtectedRoute>} />
                            <Route path="/read-explanation/:type/:id" element={<ProtectedRoute><PDFViewer /></ProtectedRoute>} />

                            {/* Board Practice Sets — primary MCQ practice flow (Step 5-6). Step 7
                                cutover: old /mcq-practice route removed below; MCQPractice.jsx stays
                                on disk (Phase 6 deletes it), unreachable now. */}
                            <Route path="/board-practice-sets" element={<BoardPracticeSets />} />
                            <Route path="/board-practice-sets/:categoryId" element={<TestSeriesListing />} />
                            <Route path="/board-checkout/:categoryId" element={<BoardCheckout />} />
                            <Route path="/board-quiz/:practiceSetId" element={<ProtectedRoute><BoardQuiz /></ProtectedRoute>} />
                            <Route path="/board-leaderboard/:practiceSetId" element={<BoardLeaderboard />} />
                            {/* Global leaderboard hub (Step 4B) — public, same as both per-test
                                leaderboards below it links to. Replaces the old MockTestLeaderboard
                                mount here. */}
                            <Route path="/leaderboard" element={<LeaderboardHub />} />

                            {/* Mock Test result + leaderboard — shared by both the old and new
                                flows (both write to the same MockTestResult collection), so these
                                stay live regardless of which browse flow led here. */}
                            <Route path="/mock-test-result/:resultId" element={<ProtectedRoute><MockTestResult /></ProtectedRoute>} />
                            {/* Old exam-picker leaderboard (no dedup, exposes email, login-gated) —
                                kept mounted, no longer linked from MockTestResult (Step 4B repointed
                                that button to /mock-leaderboard/:mockTestId below). Phase 6 deletes it. */}
                            <Route path="/mock-test-leaderboard/:examName/:testId" element={<ProtectedRoute><MockTestLeaderboard /></ProtectedRoute>} />
                            {/* New per-test Mock leaderboard (Step 4B) — Board-parity: public,
                                best-attempt dedup, name only. */}
                            <Route path="/mock-leaderboard/:mockTestId" element={<MockLeaderboard />} />

                            {/* Mock Test browse flow (Step 4-5B) — category-based, isFree/Purchase
                                gated. Step 6 cutover: old /mock-tests + /mock-test/:testId routes
                                removed above; MockTests.jsx/MockTestWindow.jsx stay on disk
                                (Phase 6 deletes them), unreachable now. This is primary. */}
                            <Route path="/mock-tests-new" element={<MockTestCategories />} />
                            <Route path="/mock-tests-new/:categoryId" element={<MockTestListing />} />
                            <Route path="/mock-checkout/:categoryId" element={<MockCheckout />} />
                            <Route path="/mock-test-new/:mockTestId" element={<ProtectedRoute><MockTestQuizNew /></ProtectedRoute>} />

                            {/* Blogs (Phase 5 Step 2) — public, no login, no purchase/gate at all. */}
                            <Route path="/blogs" element={<Blogs />} />
                            <Route path="/blogs/:slug" element={<BlogDetail />} />

                            {/* 404 Page */}
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    </main>
                    <Footer />
                </div>
            </Suspense>
        </Router>
    );
}

// Main App Component with Providers
function App() {
    return (
        <HelmetProvider>
            <AuthProvider>
                <BookCartProvider>
                    <AppContent />
                </BookCartProvider>
            </AuthProvider>
        </HelmetProvider>
    );
}

export default App;